import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarClock, Clock, FileX, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  PageHeader,
  LoadingState,
  KPIGrid,
  MetricCard,
  DataTable,
  TableFilters,
  type Column,
} from '@/components/common';
import { stagger, fadeUp } from '@/lib/animations';
import {
  equipmentApi,
  type CertificationGap,
  type CertificationGapSummary,
} from '@/api/services/equipment.api';
import { formatDate } from '@/lib/utils';
import { certificateTypeLabel } from '@/lib/certificate-types';
import { BAND_STYLE, criticalityLabel, type Criticality } from '@/lib/criticality';
import { getErrorMessage } from '@/api/client';

/**
 * Assets missing, or about to lose, a certificate their class requires.
 *
 * One row per gap, not per asset. The API returns three nested lists against
 * each asset — never obtained, lapsed, expiring — which is the right shape to
 * compute and the wrong one to work from: you cannot sort by the most overdue
 * certificate, filter to lapsed only, or count the work.
 *
 * The unit somebody acts on is asset × certificate. "RIG-001 needs API Q1" is a
 * task; "RIG-001 has three problems" is a summary of tasks. Flattened here
 * rather than server-side, because the payload already carries everything and
 * the nested shape is what the summary endpoint wants.
 *
 * Blocking comes before expiring because the difference is legal, not
 * numerical: an asset without a required certificate cannot go out at all,
 * whatever the date on the one it is missing.
 */

type GapKind = 'MISSING' | 'LAPSED' | 'EXPIRING';

type GapRow = {
  id: string;
  equipmentId: string;
  assetNumber: string;
  name: string;
  className: string;
  locationName: string | null;
  criticality: Criticality;
  kind: GapKind;
  certificateType: string;
  certificateNumber: string | null;
  /** The date that matters for this kind: expired on, or expires on. */
  date: string | null;
  /** Positive is days past expiry; negative is days remaining. */
  daysPastDue: number | null;
};

const KIND: Record<GapKind, { label: string; className: string; blocks: boolean }> = {
  MISSING: {
    label: 'Never obtained',
    className: 'bg-status-destructive-subtle text-status-destructive-foreground',
    blocks: true,
  },
  LAPSED: {
    label: 'Lapsed',
    className: 'bg-status-destructive-subtle text-status-destructive-foreground',
    blocks: true,
  },
  EXPIRING: {
    label: 'Expiring',
    className: 'bg-status-warning-subtle text-status-warning-foreground',
    blocks: false,
  },
};

const KIND_FILTER_OPTIONS = [
  { value: 'MISSING', label: 'Never obtained' },
  { value: 'LAPSED', label: 'Lapsed' },
  { value: 'EXPIRING', label: 'Expiring' },
];

const daysFrom = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

/** One asset's three lists become individual pieces of work. */
function flatten(gaps: CertificationGap[]): GapRow[] {
  const rows: GapRow[] = [];

  for (const gap of gaps) {
    const base = {
      equipmentId: gap.equipmentId,
      assetNumber: gap.assetNumber,
      name: gap.name,
      className: gap.className,
      locationName: gap.locationName,
      criticality: gap.criticality,
    };

    for (const type of gap.missing) {
      rows.push({
        ...base,
        id: `${gap.equipmentId}:missing:${type}`,
        kind: 'MISSING',
        certificateType: type,
        certificateNumber: null,
        date: null,
        daysPastDue: null,
      });
    }
    for (const l of gap.lapsed) {
      rows.push({
        ...base,
        id: `${gap.equipmentId}:lapsed:${l.type}`,
        kind: 'LAPSED',
        certificateType: l.type,
        certificateNumber: l.certificateNumber,
        date: l.expiredOn,
        daysPastDue: daysFrom(l.expiredOn),
      });
    }
    for (const e of gap.expiringSoon) {
      rows.push({
        ...base,
        id: `${gap.equipmentId}:expiring:${e.type}`,
        kind: 'EXPIRING',
        certificateType: e.type,
        certificateNumber: e.certificateNumber,
        date: e.expiresOn,
        daysPastDue: daysFrom(e.expiresOn),
      });
    }
  }

  // Blocking first, then most overdue, then the highest consequence. A missing
  // certificate has no date at all and still outranks one expiring next month.
  return rows.sort((a, b) => {
    if (KIND[a.kind].blocks !== KIND[b.kind].blocks) return KIND[a.kind].blocks ? -1 : 1;
    const overdue = (b.daysPastDue ?? 0) - (a.daysPastDue ?? 0);
    if (overdue !== 0) return overdue;
    return (b.criticality.score ?? -1) - (a.criticality.score ?? -1);
  });
}

/** What the date means for this kind of gap, quantified. */
function whenLine(row: GapRow): string {
  if (row.kind === 'MISSING') return 'required by the class, never held';
  if (row.daysPastDue == null) return '';
  if (row.kind === 'LAPSED') return `${row.daysPastDue} days past expiry`;
  const remaining = -row.daysPastDue;
  return remaining <= 0 ? 'expires today' : `expires in ${remaining} days`;
}

export default function CertificationGaps() {
  const [gaps, setGaps] = useState<CertificationGap[]>([]);
  const [summary, setSummary] = useState<CertificationGapSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');

  const load = useCallback(
    () =>
      Promise.all([equipmentApi.getCertificationGaps(), equipmentApi.getCertificationGapSummary()])
        .then(([g, s]) => {
          setGaps(g);
          setSummary(s);
        })
        .catch((error) => toast.error(getErrorMessage(error)))
        .finally(() => setLoading(false)),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => flatten(gaps), [gaps]);

  const classOptions = useMemo(
    () => [...new Set(rows.map((r) => r.className))].sort().map((c) => ({ value: c, label: c })),
    [rows]
  );

  const filtered = rows.filter((r) => {
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      r.assetNumber.toLowerCase().includes(q) ||
      r.name.toLowerCase().includes(q) ||
      certificateTypeLabel(r.certificateType).toLowerCase().includes(q);
    return (
      matchesSearch &&
      (kindFilter === 'all' || r.kind === kindFilter) &&
      (classFilter === 'all' || r.className === classFilter)
    );
  });

  const blocking = filtered.filter((r) => KIND[r.kind].blocks);
  const expiring = filtered.filter((r) => !KIND[r.kind].blocks);

  const columns: Column<GapRow>[] = [
    {
      key: 'assetNumber',
      header: 'Asset',
      sortable: true,
      linkTo: (r) => `/equipment/${r.equipmentId}`,
      render: (r) => (
        <div className="min-w-0">
          <p className="font-medium">{r.assetNumber}</p>
          <p className="text-xs text-muted-foreground">{r.name}</p>
        </div>
      ),
    },
    {
      key: 'certificateType',
      header: 'Certificate',
      sortable: true,
      render: (r) => (
        <div className="min-w-0">
          <p>{certificateTypeLabel(r.certificateType)}</p>
          {r.certificateNumber && (
            <p className="font-mono text-xs text-muted-foreground">{r.certificateNumber}</p>
          )}
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Problem',
      sortable: true,
      render: (r) => (
        <div className="min-w-0">
          <Badge className={KIND[r.kind].className}>{KIND[r.kind].label}</Badge>
          <p className="mt-1 text-xs text-muted-foreground">{whenLine(r)}</p>
        </div>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      render: (r) =>
        r.date ? (
          <span
            className={`tabular-nums ${r.kind === 'LAPSED' ? 'text-status-destructive-foreground' : ''}`}
          >
            {formatDate(r.date)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'criticality',
      header: 'Criticality',
      sortable: true,
      // Which gap to fix first is a question about consequence, not date. A
      // lapsed certificate on the BOP outranks one on a hand tool.
      accessorFn: (r) => r.criticality.score ?? -1,
      render: (r) => (
        <Badge
          variant="outline"
          className={BAND_STYLE[r.criticality.band].className}
          title={r.criticality.provenance}
        >
          {criticalityLabel(r.criticality)}
        </Badge>
      ),
    },
    {
      key: 'className',
      header: 'Class',
      sortable: true,
      render: (r) => (
        <div className="min-w-0">
          <p className="text-sm">{r.className}</p>
          {r.locationName && <p className="text-xs text-muted-foreground">{r.locationName}</p>}
        </div>
      ),
    },
  ];

  if (loading) return <LoadingState variant="detail" />;

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Equipment', href: '/equipment' },
            { label: 'Certification Gaps' },
          ]}
          title="Certification Gaps"
          description="Certificates required by an asset's class but not currently held, listed one row per gap."
        />
      </motion.div>

      {summary && (
        <motion.div variants={fadeUp}>
          <KPIGrid columns={4}>
            <MetricCard
              label="Assets affected"
              value={summary.assetsWithGaps}
              icon={<ShieldAlert className="h-5 w-5" />}
              sentiment={summary.assetsWithGaps > 0 ? 'warning' : 'success'}
            />
            <MetricCard
              label="Never obtained"
              value={summary.missingCount}
              icon={<FileX className="h-5 w-5" />}
              sentiment={summary.missingCount > 0 ? 'danger' : 'success'}
              subtitle="required by the class, not held"
            />
            <MetricCard
              label="Lapsed"
              value={summary.lapsedCount}
              icon={<Clock className="h-5 w-5" />}
              sentiment={summary.lapsedCount > 0 ? 'danger' : 'success'}
              subtitle="held, past expiry"
            />
            <MetricCard
              label="Expiring in 30 days"
              value={summary.expiringSoonCount}
              icon={<CalendarClock className="h-5 w-5" />}
              sentiment={summary.expiringSoonCount > 0 ? 'warning' : 'success'}
              subtitle="renewal takes about three weeks"
            />
          </KPIGrid>
        </motion.div>
      )}

      <motion.div variants={fadeUp}>
        <Tabs defaultValue="blocking" className="w-full">
          <TabsList>
            {/* Blocking before expiring: the difference is legal rather than
                numerical. An asset without a required certificate cannot go
                out at all. */}
            <TabsTrigger value="blocking">Blocking ({blocking.length})</TabsTrigger>
            <TabsTrigger value="expiring">Expiring ({expiring.length})</TabsTrigger>
            <TabsTrigger value="all">All gaps ({filtered.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="blocking" className="mt-6">
            <DataTable<GapRow>
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search asset or certificate..."
                  filters={[
                    {
                      id: 'kind',
                      label: 'Problems',
                      value: kindFilter,
                      options: KIND_FILTER_OPTIONS,
                      onChange: setKindFilter,
                      placeholder: 'Problem',
                    },
                    {
                      id: 'class',
                      label: 'Classes',
                      value: classFilter,
                      options: classOptions,
                      onChange: setClassFilter,
                      placeholder: 'Class',
                    },
                  ]}
                />
              }
              data={blocking}
              columns={columns}
              pagination
              exportable
              exportFilename="certification-blocking"
              columnToggle
              emptyTitle="Nothing is blocking"
              emptyDescription="Every asset holds the certificates its class requires."
            />
          </TabsContent>

          <TabsContent value="expiring" className="mt-6">
            <DataTable<GapRow>
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search asset or certificate..."
                  filters={[
                    {
                      id: 'kind',
                      label: 'Problems',
                      value: kindFilter,
                      options: KIND_FILTER_OPTIONS,
                      onChange: setKindFilter,
                      placeholder: 'Problem',
                    },
                    {
                      id: 'class',
                      label: 'Classes',
                      value: classFilter,
                      options: classOptions,
                      onChange: setClassFilter,
                      placeholder: 'Class',
                    },
                  ]}
                />
              }
              data={expiring}
              columns={columns}
              pagination
              exportable
              exportFilename="certification-expiring"
              columnToggle
              emptyTitle="Nothing expiring"
              emptyDescription="No certificate falls due inside the next thirty days."
            />
          </TabsContent>

          <TabsContent value="all" className="mt-6">
            <DataTable<GapRow>
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search asset or certificate..."
                  filters={[
                    {
                      id: 'kind',
                      label: 'Problems',
                      value: kindFilter,
                      options: KIND_FILTER_OPTIONS,
                      onChange: setKindFilter,
                      placeholder: 'Problem',
                    },
                    {
                      id: 'class',
                      label: 'Classes',
                      value: classFilter,
                      options: classOptions,
                      onChange: setClassFilter,
                      placeholder: 'Class',
                    },
                  ]}
                />
              }
              data={filtered}
              columns={columns}
              pagination
              exportable
              exportFilename="certification-gaps"
              columnToggle
              emptyTitle="No gaps"
              emptyDescription="Every asset holds what its class requires, with nothing falling due soon."
            />
          </TabsContent>
        </Tabs>
      </motion.div>
    </motion.main>
  );
}
